import { MessageCircle, Terminal, FolderGit2, QrCode, Shield, Copy, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user } = useAuth();

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Disalin ke clipboard");
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-4xl mx-auto animate-fade-up">
      <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Pengaturan</div>
      <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight c-strong mb-1">Pengaturan & Bot WhatsApp</h1>
      <p className="text-sm c-muted mb-10">Kelola akun dan hubungkan bot WhatsApp Anda.</p>

      {/* Account */}
      <div className="c-card border rounded-xl p-6 mb-6 card-elev">
        <div className="flex items-center gap-2 mb-5">
          <User className="w-4 h-4 text-gold" strokeWidth={1.6} />
          <span className="font-display text-xl font-medium c-strong">Akun</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 text-sm">
          <div><div className="c-muted text-xs uppercase tracking-[0.1em] mb-1">Nama</div><span className="c-strong">{user?.name}</span></div>
          <div><div className="c-muted text-xs uppercase tracking-[0.1em] mb-1">Email</div><span className="c-strong">{user?.email}</span></div>
          <div><div className="c-muted text-xs uppercase tracking-[0.1em] mb-1">Peran</div><span className="c-strong capitalize">{user?.role}</span></div>
        </div>
      </div>

      {/* WA Bot */}
      <div className="c-card border rounded-xl overflow-hidden card-elev">
        <div className="px-6 py-5 border-b c-border flex items-center justify-between bg-gradient-to-r from-emerald2/[0.04] to-transparent">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-emerald2 flex items-center justify-center text-white rounded-lg shadow-lg shadow-emerald2/25">
              <MessageCircle className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div>
              <div className="font-display text-xl font-medium c-strong">Bot WhatsApp</div>
              <div className="text-xs c-muted">Tanpa API resmi — berbasis whatsapp-web.js</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-3.5 py-2 bg-wine/5 text-wine dark:text-[#E08585] text-xs font-medium rounded-lg" data-testid="wa-status">
            <span className="w-2 h-2 rounded-full bg-wine dark:bg-[#E08585] pulse-dot" /> Terputus
          </span>
        </div>

        <div className="p-6 space-y-6 text-sm">
          <p className="c-muted leading-relaxed">
            Bot WhatsApp berjalan secara <strong className="c-strong">lokal di komputer Anda</strong> (bukan di server),
            menggunakan pustaka <span className="font-mono c-strong">whatsapp-web.js</span>. Setelah berjalan, klien atau staf cukup
            mengirim pesan untuk mencari dokumen dari arsip ini.
          </p>

          <Step n="1" icon={FolderGit2} title="Buka folder bot">
            Folder bot tersedia di <Code text="/app/whatsapp-bot" copy={copy} />. Salin ke komputer lokal Anda.
          </Step>

          <Step n="2" icon={Terminal} title="Install & jalankan">
            <div className="space-y-2 mt-2">
              <Code text="cd whatsapp-bot && yarn install" copy={copy} block />
              <Code text={`BACKEND_URL=${BACKEND_URL} BOT_TOKEN=<token-anda> node index.js`} copy={copy} block />
            </div>
          </Step>

          <Step n="3" icon={QrCode} title="Scan QR Code">
            QR code akan muncul di terminal. Scan dengan WhatsApp di HP (Setelan → Perangkat Tertaut).
            Setelah tertaut, status di atas menjadi <span className="text-emerald2 dark:text-[#7FBF9F] font-medium">Terhubung</span>.
          </Step>

          <div className="c-soft border c-border rounded-xl p-5">
            <div className="text-xs uppercase tracking-[0.12em] c-muted mb-3">Cara pakai dari WhatsApp</div>
            <ul className="space-y-2.5 c-text">
              <li className="flex items-start gap-2">· Ketik <span className="font-mono bg-white dark:bg-[#0E141A] px-2 py-0.5 border c-border rounded-md text-xs">cari Budi Santoso</span> → bot membalas daftar akta.</li>
              <li className="flex items-start gap-2">· Ketik <span className="font-mono bg-white dark:bg-[#0E141A] px-2 py-0.5 border c-border rounded-md text-xs">ambil 1</span> → bot mengirim berkas dokumen nomor 1.</li>
            </ul>
          </div>

          <div className="flex items-start gap-2.5 text-xs text-[#8a6d2f] dark:text-[#cBa86a] bg-gold/[0.07] border border-gold/25 rounded-xl p-4">
            <Shield className="w-4 h-4 shrink-0 mt-0.5 text-gold" />
            <span className="leading-relaxed">
              Karena tanpa API resmi, gunakan nomor WhatsApp khusus kantor. Otomasi berlebihan dapat melanggar ketentuan WhatsApp.
              Token bot disimpan aman di file <span className="font-mono">.env</span> backend.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 shrink-0 bg-ink dark:bg-[#1E262E] text-gold text-xs flex items-center justify-center rounded-lg font-mono font-semibold">{n}</div>
      <div className="flex-1 pt-0.5">
        <div className="font-medium c-strong flex items-center gap-2 mb-1.5"><Icon className="w-4 h-4 text-gold" strokeWidth={1.6} /> {title}</div>
        <div className="c-muted">{children}</div>
      </div>
    </div>
  );
}

function Code({ text, copy, block }) {
  return (
    <div className={`group inline-flex items-center gap-2 font-mono text-xs bg-ink dark:bg-[#0E141A] dark:border dark:border-white/10 text-[#E5E0D8] px-3 py-2 rounded-lg ${block ? "w-full justify-between" : ""}`}>
      <span className="break-all">{text}</span>
      <button onClick={() => copy(text)} className="opacity-50 hover:opacity-100 hover:text-gold transition shrink-0"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}
